import { useClientPortalAPI } from "@certn/api/src/useClientPortalAPI";
import {
  Card,
  Layout,
  Loader,
  SecondaryTopNav,
  SectionMessage,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { components } from "@certn/api/generated-schemas/client-portal";
import { SuccessFactorsPackagesAndTeamsList } from "./components/SuccessFactorsPackagesAndTeamsList";
import { SuccessFactorsSetupStep } from "./components/SuccessFactorsSetupStep";
import { ClientParsedOutbound } from "./components/SuccessFactorsSapCertificateFileInput";
import { SuccessFactorsIntegrationHeader } from "./SuccessFactorsIntegrationHeader";
import { successFactorsEditIntegrationStableKey } from "./successFactorsEditIntegrationStableKey";

export { successFactorsEditIntegrationStableKey } from "./successFactorsEditIntegrationStableKey";

function findSuccessFactorsIntegrationDetail(
  integrationListData: unknown
): components["schemas"]["AtsIntegrationDetail"] | undefined {
  return (
    (
      integrationListData as {
        results?: components["schemas"]["AtsIntegrationDetail"][];
      }
    )?.results || []
  ).find(
    (
      result: components["schemas"]["AtsIntegrationDetail"]
    ): result is components["schemas"]["AtsIntegrationDetail"] =>
      result.successfactors_integration !== null
  );
}

function useStableSuccessFactorsEditInitialValues(
  integrationListData: unknown
): components["schemas"]["AtsIntegrationDetail"] | undefined {
  const fromList = findSuccessFactorsIntegrationDetail(integrationListData);
  const stableRef = useRef(fromList);
  if (fromList) {
    const nextKey = successFactorsEditIntegrationStableKey(fromList);
    const prev = stableRef.current;
    const prevKey = prev ? successFactorsEditIntegrationStableKey(prev) : "";
    if (nextKey !== prevKey) {
      stableRef.current = fromList;
    }
  }
  return stableRef.current;
}

export function SuccessFactorsIntegrationEditPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [clientParsedOutbound, setClientParsedOutbound] =
    useState<ClientParsedOutbound | null>(null);
  const [certificateUploadStatus, setCertificateUploadStatus] = useState<
    string | null
  >(null);
  const { data, isLoading, mutate } = useClientPortalAPI(
    "/api/internal/client-portal/ats-integrations/"
  );
  const cachedIntegrationListRef = useRef(data);
  if (data) {
    cachedIntegrationListRef.current = data;
  }
  const integrationListData = data ?? cachedIntegrationListRef.current;
  const initialValues =
    useStableSuccessFactorsEditInitialValues(integrationListData);

  if (!integrationListData && isLoading) {
    return <Loader overlay />;
  }

  return (
    <Layout
      layout="narrow"
      backgroundColor="surface-base"
      header={
        <SecondaryTopNav
          testID="ats-integration-edit-top-nav"
          leftIcon="arrow_back"
          onleftIconOnPress={() => navigate("/settings/ats-integrations")}
          titleArea={{ title: t("Integration details") }}
        />
      }
    >
      <div className={styles.wrapper}>
        {initialValues ? (
          <>
            <SuccessFactorsIntegrationHeader
              initialValues={initialValues}
              reload={mutate}
            />
            <SuccessFactorsSetupStep
              initialValues={initialValues}
              reload={mutate}
              clientParsedOutbound={clientParsedOutbound}
              onClientParsedOutboundChange={setClientParsedOutbound}
              certificateUploadStatus={certificateUploadStatus}
              onCertificateUploadStatusChange={setCertificateUploadStatus}
            />
            <Card type="large" className={styles.card}>
              <SuccessFactorsPackagesAndTeamsList />
            </Card>
          </>
        ) : (
          <SectionMessage
            type="danger"
            sectionTitle={t("Unable to load")}
            icon="error"
          >
            <Trans>Unable to fetch the integration information.</Trans>
          </SectionMessage>
        )}
      </div>
    </Layout>
  );
}

const styles = {
  wrapper: css({
    width: "560px",
    margin: "auto",
  }),
  card: css({
    marginTop: "var(--space-4)",
    padding: "var(--space-4)",
  }),
};
